/**
 * Helpers de input para formulários financeiros — sanitizam e validam
 * para evitar valores inadequados (texto em dinheiro, % absurdo, datas fora da competência).
 */

/** Mantém só dígitos e no máx. um separador decimal ("," ou "."), com casas limitadas. */
export function sanitizeDecimalTyping(raw, { maxDecimals = 2 } = {}) {
  let s = String(raw ?? "").replace(/[^\d.,]/g, "");
  if (!s) return "";
  s = s.replace(/,/g, ".");
  const firstDot = s.indexOf(".");
  if (firstDot >= 0) {
    const intPart = s.slice(0, firstDot).replace(/\./g, "") || (s.startsWith(".") ? "0" : "");
    const decPart = s
      .slice(firstDot + 1)
      .replace(/\./g, "")
      .slice(0, maxDecimals);
    s = decPart.length || String(raw).includes(".") || String(raw).includes(",")
      ? `${intPart}.${decPart}`
      : intPart;
    // Preserve trailing separator while user is still typing ("10.")
    if ((String(raw).endsWith(".") || String(raw).endsWith(",")) && decPart === "") {
      s = `${intPart}.`;
    }
  } else {
    s = s.replace(/\./g, "");
  }
  // Avoid leading zeros like 00012 → keep as typed except bare "00"
  if (/^0\d/.test(s) && !s.startsWith("0.")) {
    s = s.replace(/^0+/, "") || "0";
  }
  return s;
}

export function parseDecimal(raw) {
  if (raw == null || raw === "") return null;
  const n = Number(String(raw).trim().replace(",", "."));
  if (!Number.isFinite(n)) return null;
  return n;
}

/** Valor monetário em reais (≥ 0). null = vazio. */
export function parseMoneyReais(raw) {
  const n = parseDecimal(raw);
  if (n == null) return null;
  if (n < 0) return null;
  return Math.round(n * 100) / 100;
}

export function moneyReaisToCents(raw) {
  const n = parseMoneyReais(raw);
  if (n == null) return null;
  return Math.round(n * 100);
}

/** Fração 0–1 a partir de input em % (ex.: "10" → 0.1). */
export function pctToInput(v) {
  if (v == null || v === "") return "";
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return String(Math.round(n * 10000) / 100);
}

export function inputToPct(v, { maxPct = 100, allowEmpty = true } = {}) {
  if (v == null || v === "") return allowEmpty ? null : 0;
  const n = parseDecimal(v);
  if (n == null) return allowEmpty ? null : 0;
  const clamped = Math.max(0, Math.min(maxPct, n));
  return clamped / 100;
}

export function sanitizePctTyping(raw, { maxPct = 100 } = {}) {
  const s = sanitizeDecimalTyping(raw, { maxDecimals: 2 });
  if (s === "" || s.endsWith(".")) return s;
  const n = parseDecimal(s);
  if (n == null) return "";
  if (n > maxPct) return String(maxPct);
  if (n < 0) return "0";
  return s;
}

export function clampInt(raw, { min = 0, max = 9999, fallback = min } = {}) {
  if (raw === "" || raw == null) return fallback;
  const n = Math.round(Number(String(raw).replace(/[^\d-]/g, "")));
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, n));
}

export function sanitizeIntTyping(raw) {
  return String(raw ?? "").replace(/[^\d]/g, "");
}

export function yearMonthBounds(yearMonth) {
  const ym = String(yearMonth || "").slice(0, 7);
  if (!/^\d{4}-\d{2}$/.test(ym)) return { min: undefined, max: undefined };
  const [y, m] = ym.split("-").map(Number);
  const last = new Date(y, m, 0).getDate();
  return {
    min: `${ym}-01`,
    max: `${ym}-${String(last).padStart(2, "0")}`,
  };
}

export function isDateInYearMonth(dateStr, yearMonth) {
  if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return false;
  const { min, max } = yearMonthBounds(yearMonth);
  if (!min || !max) return false;
  return dateStr >= min && dateStr <= max;
}

export const FIELD_LIMITS = {
  teamCommissionPctMax: 50, // closer/indicator mensal sobre base
  leaderOverridePctMax: 100, // % da comissão do liderado
  channelParticipationPctMax: 100,
  channelRoyaltyPctMax: 100,
  channelGrowthPctMax: 100,
  poolCapPctMax: 100,
  payoutMonthsMin: 1,
  payoutMonthsMax: 60,
  channelWindowMonthsMax: 120,
  notesMax: 500,
  moneyMaxReais: 10_000_000,
};
